import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { 
  Calendar, 
  Download,
  Plus,
  Edit,
  Trash2,
  Search,
  DollarSign,
  TrendingUp,
  Percent,
  ShoppingBag,
  AlertTriangle
} from 'lucide-react';

import { 
  fetchExpenses, 
  fetchExpenseSummary, 
  createExpense, 
  updateExpense, 
  deleteExpense,
  setExpenseFilter,
  resetExpenseFilter
} from '../../redux/actions/businessExpenseActions';

const AdminFinancePanel = () => {
  const dispatch = useDispatch();
  
  // Estados de Redux
  const { 
    expenses, 
    summary, 
    totalCount, 
    currentPage, 
    totalPages, 
    filters, 
    loading, 
    error 
  } = useSelector((state) => state.businessExpenses || {});
  
  const orders = useSelector((state) => state.orders?.allOrders || []);
  
  // Estados locales
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    precio: '',
    titulo: '',
    descripcion: '',
    otros: {}
  });
  
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  
  // Calcular ingresos totales de las órdenes (excluyendo estados específicos)
  const calculateTotalRevenue = () => {
    // Estados a excluir en el cálculo de ingresos
    const excludedStatuses = ['Pago pendiente', 'Pedido cancelado'];
    
    // Filtrar órdenes válidas
    const validOrders = orders.filter(order => 
      !excludedStatuses.includes(order.status)
    );
    
    // Aplicar filtro de fechas si existe
    const filteredOrders = validOrders.filter(order => {
      const orderDate = new Date(order.createdAt || order.created_at);
      const orderDateString = orderDate.toISOString().split('T')[0];
      
      const matchesDateRange = (
        !dateRange.start || orderDateString >= dateRange.start
      ) && (
        !dateRange.end || orderDateString <= dateRange.end
      );

      return matchesDateRange;
    });
    
    // Calcular ingresos totales
    return filteredOrders.reduce((total, order) => total + (parseFloat(order.total) || 0), 0);
  };
  
  // Calcular gastos de órdenes
  const calculateOrderExpenses = () => {
    const excludedStatuses = ['Pago pendiente', 'Pedido cancelado'];
    
    // Filtrar órdenes válidas
    const validOrders = orders.filter(order => 
      !excludedStatuses.includes(order.status)
    );
    
    // Aplicar filtro de fechas si existe
    const filteredOrders = validOrders.filter(order => {
      const orderDate = new Date(order.createdAt || order.created_at);
      const orderDateString = orderDate.toISOString().split('T')[0];
      
      const matchesDateRange = (
        !dateRange.start || orderDateString >= dateRange.start
      ) && (
        !dateRange.end || orderDateString <= dateRange.end
      );

      return matchesDateRange;
    });
    
    // Calcular gastos de órdenes
    let totalOrderExpenses = 0;
    
    filteredOrders.forEach(order => {
      if (order.gastos) {
        Object.values(order.gastos).forEach(amount => {
          totalOrderExpenses += parseFloat(amount) || 0;
        });
      }
    });
    
    return totalOrderExpenses;
  };
  
  // Calcular ganancias de órdenes (ingresos - gastos)
  const calculateOrderProfit = () => {
    const totalRevenue = calculateTotalRevenue();
    const totalOrderExpenses = calculateOrderExpenses();
    return totalRevenue - totalOrderExpenses;
  };
  
  // Calcular ganancia neta (ganancias de órdenes - gastos del negocio)
  const calculateNetProfit = () => {
    const orderProfit = calculateOrderProfit();
    const businessExpenses = summary?.totalExpense || 0;
    return orderProfit - businessExpenses;
  };
  
  // Cargar datos iniciales
  useEffect(() => {
    dispatch(fetchExpenses());
    dispatch(fetchExpenseSummary());
  }, [dispatch]);
  
  // Actualizar datos cuando cambien los filtros de fecha
  useEffect(() => {
    dispatch(setExpenseFilter({
      startDate: dateRange.start,
      endDate: dateRange.end,
      search: searchTerm
    }));
    
    dispatch(fetchExpenses({
      startDate: dateRange.start,
      endDate: dateRange.end,
      search: searchTerm
    }));
    
    dispatch(fetchExpenseSummary({
      startDate: dateRange.start,
      endDate: dateRange.end
    }));
  }, [dispatch, dateRange, searchTerm]);
  
  // Manejar cambio de página
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      dispatch(setExpenseFilter({ page: newPage }));
      dispatch(fetchExpenses({ ...filters, page: newPage }));
    }
  };
  
  // Manejar cambios en el formulario
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setExpenseForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Manejar envío del formulario
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedExpense) {
        // Actualizar gasto existente
        await dispatch(updateExpense(selectedExpense.id, expenseForm));
        toast.success('Gasto actualizado correctamente');
      } else {
        // Crear nuevo gasto
        await dispatch(createExpense(expenseForm));
        toast.success('Gasto creado correctamente');
      }
      
      // Recargar datos
      dispatch(fetchExpenses(filters));
      dispatch(fetchExpenseSummary({
        startDate: dateRange.start,
        endDate: dateRange.end
      }));
      
      // Limpiar formulario
      setExpenseForm({
        fecha: new Date().toISOString().split('T')[0],
        precio: '',
        titulo: '',
        descripcion: '',
        otros: {}
      });
      
      setSelectedExpense(null);
      setShowExpenseForm(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar gasto');
    }
  };
  
  // Abrir formulario para editar
  const handleEditExpense = (expense) => {
    setSelectedExpense(expense);
    setExpenseForm({
      fecha: new Date(expense.fecha).toISOString().split('T')[0],
      precio: expense.precio,
      titulo: expense.titulo,
      descripcion: expense.descripcion || '',
      otros: expense.otros || {}
    });
    setShowExpenseForm(true);
  };
  
  // Eliminar gasto
  const handleDeleteExpense = async (id) => {
    if (window.confirm('¿Estás seguro que deseas eliminar este gasto?')) {
      try {
        await dispatch(deleteExpense(id));
        toast.success('Gasto eliminado correctamente');
        
        // Recargar datos
        dispatch(fetchExpenses(filters));
        dispatch(fetchExpenseSummary({
          startDate: dateRange.start,
          endDate: dateRange.end
        }));
      } catch (error) {
        toast.error(error.response?.data?.error || 'Error al eliminar gasto');
      }
    }
  };
  
  // Limpiar filtros
  const handleClearFilters = () => {
    setDateRange({ start: '', end: '' });
    setSearchTerm('');
    dispatch(resetExpenseFilter());
    dispatch(fetchExpenses());
    dispatch(fetchExpenseSummary());
  };
  
  // Exportar datos a CSV
  const handleExportData = () => {
    const headers = ['Fecha', 'Título', 'Descripción', 'Precio'];
    
    // Transformar datos
    const csvData = expenses.map(expense => [
      new Date(expense.fecha).toLocaleDateString(),
      expense.titulo,
      expense.descripcion || '',
      expense.precio
    ]);
    
    // Añadir headers
    csvData.unshift(headers);
    
    // Convertir a formato CSV
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    
    // Crear archivo para descargar
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    // Crear enlace temporal y simular clic
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `gastos_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Formatear valor a moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(value);
  };
  
  // Renderizar paginación
  const renderPagination = () => {
    const pageNumbers = [];
    const maxPageButtons = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    return (
      <div className="flex justify-center mt-4">
        <div className="flex space-x-1">
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded ${
              currentPage === 1
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            &laquo;
          </button>
          
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded ${
              currentPage === 1
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            &lt;
          </button>
          
          {pageNumbers.map(number => (
            <button
              key={number}
              onClick={() => handlePageChange(number)}
              className={`px-3 py-1 rounded ${
                currentPage === number
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {number}
            </button>
          ))}
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded ${
              currentPage === totalPages
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            &gt;
          </button>
          
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded ${
              currentPage === totalPages
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            &raquo;
          </button>
        </div>
      </div>
    );
  };
  
  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-6">Panel Financiero</h1>
      
      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Ingresos */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center mb-2">
            <DollarSign className="h-5 w-5 text-green-500 mr-2" />
            <h2 className="text-lg font-semibold">Ingresos totales</h2>
          </div>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(calculateTotalRevenue())}</p>
        </div>
        
        {/* Gastos */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center mb-2">
            <ShoppingBag className="h-5 w-5 text-red-500 mr-2" />
            <h2 className="text-lg font-semibold">Gastos totales</h2>
          </div>
          <p className="text-2xl font-bold text-red-600">
            {formatCurrency(parseFloat(summary?.totalExpense || 0) + calculateOrderExpenses())}
          </p>
        </div>
        
        {/* Ganancias brutas */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center mb-2">
            <TrendingUp className="h-5 w-5 text-blue-500 mr-2" />
            <h2 className="text-lg font-semibold">Ganancias brutas</h2>
          </div>
          <p className="text-2xl font-bold text-blue-600">
            {formatCurrency(calculateOrderProfit())}
          </p>
        </div>
        
        {/* Ganancia neta */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center mb-2">
            <Percent className="h-5 w-5 text-purple-500 mr-2" />
            <h2 className="text-lg font-semibold">Ganancia neta</h2>
          </div>
          <p className="text-2xl font-bold text-purple-600">
            {formatCurrency(calculateNetProfit())}
          </p>
        </div>
      </div>
      
      {/* Filtros y acciones */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Filtro de fecha inicio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha inicio
            </label>
            <div className="relative">
              <Calendar className="absolute left-2 top-2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="pl-10 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          {/* Filtro de fecha fin */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha fin
            </label>
            <div className="relative">
              <Calendar className="absolute left-2 top-2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="pl-10 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          {/* Búsqueda */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar gasto
            </label>
            <div className="relative">
              <Search className="absolute left-2 top-2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por título o descripción"
                className="pl-10 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
        
        {/* Botones de acción */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              setSelectedExpense(null);
              setExpenseForm({
                fecha: new Date().toISOString().split('T')[0],
                precio: '',
                titulo: '',
                descripcion: '',
                otros: {}
              });
              setShowExpenseForm(true);
            }}
            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo gasto
          </button>
          
          <button
            onClick={handleExportData}
            className="flex items-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar datos
          </button>
          
          <button
            onClick={handleClearFilters}
            className="flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Limpiar filtros
          </button>
        </div>
      </div>
      
      {/* Formulario de gasto */}
      {showExpenseForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h2 className="text-xl font-bold mb-4">
              {selectedExpense ? 'Editar gasto' : 'Nuevo gasto'}
            </h2>
            
            <form onSubmit={handleFormSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Fecha */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha *
                  </label>
                  <input
                    type="date"
                    name="fecha"
                    value={expenseForm.fecha}
                    onChange={handleFormChange}
                    required
                    className="px-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                {/* Precio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio *
                  </label>
                  <input
                    type="number"
                    name="precio"
                    value={expenseForm.precio}
                    onChange={handleFormChange}
                    required
                    min="0"
                    step="0.01"
                    className="px-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              {/* Título */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título *
                </label>
                <input
                  type="text"
                  name="titulo"
                  value={expenseForm.titulo}
                  onChange={handleFormChange}
                  required
                  className="px-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Descripción */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  name="descripcion"
                  value={expenseForm.descripcion}
                  onChange={handleFormChange}
                  rows="3"
                  className="px-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>
              
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowExpenseForm(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Cancelar
                </button>
                
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {selectedExpense ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Tabla de gastos */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <h2 className="text-lg font-semibold p-4 border-b">Historial de gastos</h2>
        
        {loading ? (
          <div className="p-6 text-center">Cargando datos...</div>
        ) : error ? (
          <div className="p-6 text-center text-red-500 flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            {error}
          </div>
        ) : expenses?.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No hay gastos registrados</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Título</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {expenses?.map((expense) => (
                  <tr key={expense.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {new Date(expense.fecha).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm">{expense.titulo}</td>
                    <td className="px-4 py-3 text-sm">{expense.descripcion || '-'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-red-600">
                      {formatCurrency(expense.precio)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditExpense(expense)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteExpense(expense.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Paginación */}
        {!loading && !error && expenses?.length > 0 && totalPages > 1 && renderPagination()}
      </div>
    </div>
  );
};

export default AdminFinancePanel;